import { Patient } from '../components/patients/PatientCard';

export interface PatientComplaint {
  id?: number;
  patient_id: number;
  complaint: string;
  
  // Patient information
  patient?: Patient;
  
  // Recording information
  recorded_by?: {
    id: number;
    name: string;
    role: string;
  };
  
  recorded_at: string;
  recorded_at_formatted?: string;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
  
  // Additional computed fields
  time_since_recorded?: string;
  is_recent?: boolean;
}

export interface CreateComplaintData {
  patient_id: number;
  complaint: string;
  // recorded_at is automatically set to current timestamp on server
}

export interface UpdateComplaintData {
  complaint?: string;
  // recorded_at cannot be updated - timestamp is locked when complaint is created
}

export interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

class ComplaintService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(`${this.BASE_URL}${url}`, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  }

  // Get complaints with filtering
  static async getComplaints(params?: {
    patient_id?: number;
    recorded_by?: number;
    date_from?: string;
    date_to?: string;
    recent_days?: number;
    search?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<LaravelPaginatedResponse<PatientComplaint>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `/complaints${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await this.request(url);
  }

  // Get single complaint
  static async getComplaint(id: number): Promise<PatientComplaint> {
    const result = await this.request(`/complaints/${id}`);
    return result.data;
  }

  // Create new complaint
  static async createComplaint(data: CreateComplaintData): Promise<PatientComplaint> {
    try {
      const result = await this.request('/complaints', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Create complaint error:', error);
      throw error;
    }
  }

  // Update complaint
  static async updateComplaint(id: number, data: UpdateComplaintData): Promise<PatientComplaint> {
    const result = await this.request(`/complaints/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  // Delete complaint
  static async deleteComplaint(id: number): Promise<void> {
    await this.request(`/complaints/${id}`, {
      method: 'DELETE',
    });
  }

  // Get patient's complaints
  static async getPatientComplaints(patientId: number, limit: number = 10): Promise<PatientComplaint[]> {
    try {
      const result = await this.request(`/complaints/patient/${patientId}?limit=${limit}`);
      
      if (result.success && result.data) {
        return Array.isArray(result.data) ? result.data : [];
      } else if (result.data && Array.isArray(result.data)) {
        return result.data;
      } else if (Array.isArray(result)) {
        return result;
      } else {
        console.warn('Unexpected response structure:', result);
        return [];
      }
    } catch (error) {
      console.error('Get patient complaints error:', error);
      throw error;
    }
  }

  // Get latest complaint for patient
  static async getLatestComplaint(patientId: number): Promise<PatientComplaint | null> {
    try {
      const result = await this.request(`/complaints/patient/${patientId}/latest`);
      return result.data;
    } catch (error) {
      console.error('Get latest complaint error:', error);
      return null;
    }
  }

  // Get complaint statistics
  static async getStatistics(): Promise<any> {
    const result = await this.request('/complaints/analytics/statistics');
    return result.data;
  }

  // Helper methods for formatting
  static formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  

  // Generate PDF for complaint - opens print view in new window
  static generatePDF(id: number): void {
    // Remove trailing slash from base URL to avoid double slashes
    const backendUrl = (process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api').replace(/\/$/, '');
    const printUrl = `${backendUrl}/complaints/${id}/print`;
    window.open(printUrl, '_blank');
  }

  // Alternative method to open print view
  static openPrintView(id: number): void {
    const backendUrl = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';
    const printUrl = `${backendUrl}/complaints/${id}/print`;
    window.open(printUrl, '_blank');
  }
}

export default ComplaintService;